import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { APIStatusCode, MisType } from "../../../../app/enums";
import IExtractClient, { IConfigurationRequest } from "../../../../models/ExtractClient";
import { ServiceManager } from "../../../../services/ServiceManager";
import { useParams } from "react-router-dom";
import { handleApiErrors } from "../../../../app/common/helper/ApiError";
import { SubmitHandler, useForm } from "react-hook-form";
import { Col, Container, Dropdown, Row, Spinner, Stack } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import MISButton from "../../../../components/button";
import Layout from "../../../layout";
import ArborProviderConfigEdit from "../../../../components/misconfig/arbor/arbor-providerconfig-edit";
import BromcomProviderConfigEdit from "../../../../components/misconfig/bromcom/bromcom-providerconfig-edit";
import SimsProviderConfigEdit from "../../../../components/misconfig/sims/sims-providerconfig-edit";

interface IExtractClientUpsertProps {
    extractClient?: IExtractClient;
    upsertExtractClient: (show: boolean) => void;
}

// Extract client Add/Edit form
export default function ExtractClientUpsert(props: IExtractClientUpsertProps) {
    const queryClient = useQueryClient();
    const { establishmentId } = useParams();
    const [selectedMisType, setSelectedMisType] = useState<MisType>(MisType.Unknown);
    
    const unsavedChangeMessage = "You have unsaved changes. Are you sure you want to leave?";
    let unsavedChanges = false;
    const providerConfigRef = useRef<any>(null);

    const mutation = useMutation({
        mutationFn: (model: IConfigurationRequest) => {
            if (props.extractClient?.clientConfiguration?.misConfigurationID && props.extractClient.clientConfiguration.misConfigurationID > 0) {
                return ServiceManager.ConfigurationService.UpdateConfigurations(establishmentId, props.extractClient.clientConfiguration.misConfigurationID, model);
            }
            return ServiceManager.ConfigurationService.CreateConfigurations(establishmentId, model);
        },
        onError: (error, variables, context) => {

        },
        onSuccess: (data, variables, context) => {
            if (data === null) {
                ServiceManager.ToastService.showError("Unable to create MIS Client.");
            }
            else {
                if (data.status === APIStatusCode.Created) {
                    ServiceManager.ToastService.showSuccess("MIS Client created successfully.");
                    queryClient.invalidateQueries({ queryKey: [`extract-clients-view-${establishmentId}`] });
                    props.upsertExtractClient(false);
                }
                else if (data.status === APIStatusCode.Ok) {
                    unsavedChanges = false;
                    ServiceManager.ToastService.showSuccess("MIS Client updated successfully.");
                    queryClient.invalidateQueries({
                        queryKey: [`extract-client-details-${establishmentId}-${props.extractClient?.clientConfiguration?.misConfigurationID}`],
                    });
                    props.upsertExtractClient(false);
                }
                else {
                    handleApiErrors(data, ServiceManager.ToastService);
                }
            }
        }
    });

    type FormValues = {
        misType: MisType;
        configJson?: string;
        createFaceTemplates: boolean;
        arborApplicationID: string | null;
    };

    const defaultValues: FormValues = (() => {
        if (!props.extractClient || !props.extractClient.clientConfiguration) return {
            misType: MisType.Unknown,
            createFaceTemplates: false,
            arborApplicationID: null
        };

        const { misTypeID, settingsJson, createFaceTemplates, arborApplicationID } = props.extractClient.clientConfiguration;
        return { misType: misTypeID, configJson: settingsJson || "", createFaceTemplates: createFaceTemplates ?? false, arborApplicationID: arborApplicationID ?? null };
    })();

    const { register, handleSubmit, clearErrors, setValue, setError, watch, formState: { errors } } = useForm<FormValues>({
        defaultValues
    });

    // Set selectedMisType from default values on mount
    useState(() => {
        if (defaultValues.misType && (defaultValues.misType as MisType) !== MisType.Unknown) {
            setSelectedMisType(defaultValues.misType as MisType);
        }

        if (props.extractClient && props.extractClient.clientConfiguration && props.extractClient.clientConfiguration.misTypeID) {
            const handleBeforeUnload = (event: any) => {
                handleUnsavedChanges(event);
            };
            window.addEventListener('beforeunload', handleBeforeUnload);
            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
            };
        }
    });

    const handleUnsavedChanges = (event: any) => {
        if (unsavedChanges) {
            const message = unsavedChangeMessage;
            event.returnValue = message;
            return message;
        }
    }

    const onSubmit: SubmitHandler<FormValues> = (data) => {
        const isValid = providerConfigRef.current?.validate();
        if (!isValid) {
            return;
        }
        if(data.misType === MisType.Unknown) {
            setError('misType', { type: 'manual', message: 'MIS type is required' });
            return;
        }

        const misTypeID = Number(data.misType);
        const model : IConfigurationRequest = {
            misTypeID,
            settingsJson: data.configJson || '',
            createFaceTemplates: data.createFaceTemplates === true || data.createFaceTemplates?.toString() === 'true',
            arborApplicationID: misTypeID === MisType.Arbor ? data.arborApplicationID as any : null
        };
        
        mutation.mutate(model);
    };

    const firstCol = 2, secondCol = 4;

    const handleSelect = (eventKey: MisType) => {
        setValue('misType', eventKey);
        setSelectedMisType(eventKey);
        clearErrors('misType');
    };

    const handleUnsavedChangesClick = () => {
        if (unsavedChanges) {
            const shouldLeave = window.confirm(unsavedChangeMessage);
            if (shouldLeave) {
                props.upsertExtractClient(false);
            }
        }
        else {
            props.upsertExtractClient(false);
        }
    }

    const calculateMaxHeight = () => {
        const viewportHeight = window.innerHeight;
        const maxHeightPercentage = 75;
        return `${(viewportHeight * maxHeightPercentage) / 100}px`;
    };

    return (
        <Layout title={props.extractClient ? "Edit MIS Client Details" : "Add New MIS Client"} backArrow={<FontAwesomeIcon icon={faArrowLeft} onClick={handleUnsavedChangesClick} className="me-4 fs-5 cursor-pointer" />}>
            <Stack>
                <form onSubmit={handleSubmit(onSubmit)} className="form">
                    <Container className="mt-3" style={{ height: calculateMaxHeight(), overflowY: 'auto' }}>
                        <Row className="pb-3">
                            <Col md={firstCol} className="pt-2"><b>MIS Type</b></Col>
                            <Col md={secondCol}>
                                <Dropdown className="m-auto" onSelect={(eventKey) => handleSelect(Number(eventKey) as MisType)}>
                                    <Dropdown.Toggle className="btn btn-light w-100 d-flex justify-content-between align-items-center border"
                                        {...register('misType', { required: 'MIS type is required' })} disabled={props.extractClient !== undefined}>
                                        {selectedMisType ? MisType[selectedMisType]?.toUpperCase() : 'Select'}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="timer-dropdown w-100">
                                        <Dropdown.Item eventKey={MisType.Arbor}>Arbor</Dropdown.Item>
                                        <Dropdown.Item eventKey={MisType.Bromcom}>Bromcom</Dropdown.Item>
                                        <Dropdown.Item eventKey={MisType.Sims}>SIMS</Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                                {errors.misType && <div className="invalid-feedback" style={{ display: 'block', color: '#DC3245' }}>MIS Type is required</div>}
                            </Col>
                        </Row>
                        <input type="hidden" {...register("configJson")} />
                        <input type="hidden" {...register("arborApplicationID")} />
                        {
                            selectedMisType === MisType.Sims ?
                                <SimsProviderConfigEdit ref={providerConfigRef} configJson={ watch('configJson') } setJsonString = { value => setValue('configJson', value) } firstCol={firstCol} secondCol={secondCol} />
                                : selectedMisType === MisType.Bromcom ?                                
                                <BromcomProviderConfigEdit ref={providerConfigRef} configJson={ watch('configJson') } setJsonString = { value => setValue('configJson', value) } firstCol={firstCol} secondCol={secondCol} />
                                : selectedMisType === MisType.Arbor ?
                                <ArborProviderConfigEdit ref={providerConfigRef} configJson={ watch('configJson') } setJsonString = { value => setValue('configJson', value) } arborApplicationID={watch('arborApplicationID') as any} setArborApplicationId={(value) => setValue('arborApplicationID', value)} firstCol={firstCol} secondCol={secondCol} />
                                : null
                            }

                        {
                            !mutation.isPending
                                ? (
                                    <Row className="mt-5">
                                        <Col md={firstCol}></Col>
                                        <Col md={secondCol} className="d-flex justify-content-end">
                                            <MISButton text="Cancel" className="w-100 btn popup-btn right-margin10 btn-outline-secondary" type="button" FnOnClick={handleUnsavedChangesClick} />
                                            <MISButton text={props.extractClient ? "Save" : "Create"} className="w-100 popup-btn btn btn-primary crbc-bg-color" type="submit" FnOnClick={handleSubmit(onSubmit)} />
                                        </Col>
                                    </Row>
                                )
                                : (
                                    <Row>
                                        <Col md={firstCol}></Col>
                                        <Col md={secondCol}>
                                            <div className="d-flex justify-content-center mt-3">
                                                <Spinner animation="border" variant="info" role="status" > </Spinner>
                                            </div>
                                        </Col>
                                    </Row>
                                )
                        }
                    </Container>
                </form>
            </Stack>
        </Layout>
    );
}